import { GetGeneralInformationResponse } from "@1games-ru/region-shared/dist/backend/requests/faction";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import FactionMpProvider from "@/core/data-provider/data-mp/faction/faction-mp-provider";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import toast from "@/domains/toasts/helpers/toast";

export const QUERY_KEY_FACTION_GENERAL_INFO = "QUERY_KEY_FACTION_GENERAL_INFO";

export default function useGetInfo() {
  const { t, i18n } = useTranslation("faction");

  const query = useQuery<GetGeneralInformationResponse>({
    queryKey: [QUERY_KEY_FACTION_GENERAL_INFO],
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      const response = await FactionMpProvider.getGeneralInformation();

      if (response instanceof RpcMpProviderSuccessResponse) {
        return response.data;
      }

      const messageKey = response?.error?.message ?? "Unknown";
      const knownI18nKey = i18n.exists(`faction:faction.errors.${messageKey}`);

      const error = new Error(messageKey) as Error & { knownI18nKey?: boolean };
      error.knownI18nKey = knownI18nKey;
      throw error;
    },
  });

  useEffect(() => {
    if (!query.isError) return;
    const error = query.error as Error & { knownI18nKey?: boolean };
    console.error(error);

    if (error.knownI18nKey) {
      toast.error(t(`faction.errors.${error.message}`));
    } else {
      toast.error(t("faction.toast.infoLoadError"));
    }
  }, [query.isError, query.error, t]);

  return {
    isLoading: query.isPending || query.isFetching,
    data: query.data,
    refetch: query.refetch,
  };
}
